import groupBy from 'helpers/groupBy';
import sortBy from 'helpers/sortBy';
import { PositionVariations } from 'types/shared';

/**
 * Return true if object has a currentPosition/peakPosition not null
 */
export const hasNoNullValue = (positionValue: any): boolean =>
  positionValue !== null;

/**
 * Sum the value of chartStreams property in an array of objects
 */
export const sumChartStreams = (arr: any[]): number =>
  arr.reduce(
    (accumulator, { chartStreams = 0 }) => accumulator + chartStreams,
    0
  );

/**
 * Flatten the 'charts' array: make one row per combination of:
 * - chart id
 * - product id
 * - territory
 * And filter out territories with currentPosition/peakPosition = null
 */
export const flattenCharts = (
  charts: any[],
  positionKey: PositionVariations
): any[] =>
  charts.reduce((acc, { territoryData, ...metadataAndGlobalData }) => {
    // Remove territories from territoryData that have currentPosition === null
    // Remove the 'other' territory
    const filteredTerritoryData = territoryData.filter(
      (territory: any) =>
        hasNoNullValue(territory[positionKey]) &&
        territory.countryId !== 'other'
    );

    // Flatten the rest of the territories
    const flattenTerritories = filteredTerritoryData.map((territory: any) => {
      return { ...metadataAndGlobalData, ...territory };
    });

    return [...acc, ...flattenTerritories];
  }, []);

/**
 * Moves the territory with countryid = 'WW' to top of territory array
 */
export const globalPositionFirst = (territories: any): any[] => {
  // Get index of territory with countryId = 'WW'
  const globalIndex = territories.findIndex(
    ({ countryId }: { countryId: string }) => countryId === 'WW'
  );
  if (globalIndex < 0) return territories;

  // Move this territory to top of the array
  return moveItemAtIndexToFirstPosition(territories, globalIndex);
};

/**
 * Small function that moves the item at the given index in an array
 * to the index 0 of that array
 */
export const moveItemAtIndexToFirstPosition = (
  arr: any[],
  index: number
): any[] => {
  if (index >= arr.length || index < 0) return arr;

  const first = arr[index];
  const rest = arr.slice(0, index).concat(arr.slice(index + 1, arr.length + 1)); // Left and right

  // TO SORT TERRITORIES, ADD CODE HERE:
  // rest.sort(...)

  return [].concat(first).concat(rest as any);
};

/**
 * Moves shared properties in nested group to parent
 * Remove those properties from the nested group
 * Aggregate totalChartStreams and totalNumberOfCountries
 */
const formatRows = ({
  territoryData,
  ...rest
}: {
  territoryData: any;
}): any => {
  if (territoryData.length === 0) return { ...rest, territoryData };

  const {
    chartName,
    chartDisplayName,
    customerId,
    customerName,
    productType,
    productDisplayName,
  } = territoryData[0];
  let { totalNumberOfCountries, totalChartStreams } = territoryData[0];

  // Aggregate
  totalNumberOfCountries = territoryData.length;
  totalChartStreams = sumChartStreams(territoryData);

  // Move global territory to top of territory array
  const sortedTerritories = globalPositionFirst(territoryData);

  return {
    ...rest,
    chartName,
    chartDisplayName,
    customerId,
    customerName,
    productType,
    productDisplayName,
    totalNumberOfCountries,
    totalChartStreams,
    territoryData: sortedTerritories,
  };
};

/**
 * This function takes the base chart DSPs the current entity appears on,
 * creates a flattened array with objects for every chart per country,
 * and groups them by position. For example: #2 for 'Spotify Top 200'
 * contains Spain and Belgium. It is possible multiple entries exist
 * for #2, each for a different DSP.
 *
 */
export default function groupByPositions(
  charts: any[],
  positionKey: PositionVariations
): any[] {
  if (charts === null) return charts;

  // Group the resultset by provided keys
  const groupedCharts = groupBy(
    flattenCharts(charts, positionKey),
    [positionKey, 'productId', 'chartId'],
    'territoryData'
  );

  // Sort result set
  groupedCharts.sort(sortBy([positionKey, 'productId', 'chartId']));

  // Move properties in nested group to parent group
  // Remove these properties from nested group
  return groupedCharts.map(formatRows);
}
